import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { API_BASE_URL } from '@/constants/config';

// ========== Types ==========

export interface UnifiedUser {
  id: string;
  email: string | null;
  phone: string | null;
  roles: string[];
  godMode?: boolean;
  displayName: string | null;
  onboardingState: Record<string, string>;
  emailVerified: boolean;
  whatsappVerified: boolean;
  entities: {
    driverId: string | null;
    flotilleroId: string | null;
    clientId: string | null;
    fleetIds?: string[];
  };
}

interface UnifiedAuthContextValue {
  user: UnifiedUser | null;
  loading: boolean;
  error: string | null;
  login: (identifier: string, credential: string, method: 'password' | 'otp') => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  refreshProfile: () => Promise<void>;
}

const TOKEN_KEY = 'nexus_token';

const Ctx = createContext<UnifiedAuthContextValue | null>(null);

// ========== Provider ==========

export function UnifiedAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UnifiedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }

    try {
      const res = await fetch(`${API_BASE_URL}/api/unified/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Invalid session');
      const json = await res.json();
      if (json.success && json.data) {
        setUser(json.data as UnifiedUser);
      } else {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      }
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const login = useCallback(async (identifier: string, credential: string, method: 'password' | 'otp') => {
    setError(null);
    const body = method === 'password'
      ? { email: identifier, password: credential }
      : { phone: identifier, otp_token: credential };

    const res = await fetch(`${API_BASE_URL}/api/unified/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const json = await res.json();

    if (!res.ok || !json.success) {
      const msg = json.message || 'Error al iniciar sesión';
      setError(msg);
      throw new Error(msg);
    }

    localStorage.setItem(TOKEN_KEY, json.data.token);
    setUser(json.data.user as UnifiedUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const hasRole = useCallback((role: string) => {
    if (user?.godMode) return true;
    return user?.roles.includes(role) ?? false;
  }, [user]);

  return (
    <Ctx.Provider value={{ user, loading, error, login, logout, hasRole, refreshProfile: fetchProfile }}>
      {children}
    </Ctx.Provider>
  );
}

// ========== Hook ==========

export function useUnifiedAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useUnifiedAuth must be used within UnifiedAuthProvider');
  return ctx;
}
